import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import KpiCard from "@/components/KpiCard";
import TrustRadar from "@/components/TrustRadar";
import UpgradeGate from "@/components/UpgradeGate";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Calendar, CheckCircle, XCircle, Star } from "lucide-react";
import api from "@/lib/api";

export default function DoctorAnalytics() {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/doctor/analytics");
      setAnalytics(data);
    })();
  }, []);

  if (!analytics) return <AppShell pageTitle="Practice analytics"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  const criteria = Object.entries(analytics.trust_metrics || {}).map(([name, score]) => ({ name, score }));

  return (
    <AppShell pageTitle="Practice analytics" pageDescription="Verified outcomes, benchmarked and visualized.">
      <UpgradeGate
        feature="Practice Analytics"
        description="Weekly appointment trends, trust-metric radar, and completion analytics — unlock the full dashboard with Doctor Pro."
        ctaLabel="Unlock Practice Analytics"
        testid="analytics-upgrade-gate"
      >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Trust Index" value={analytics.trust_score} icon={Star} tone="primary" testid="analytics-kpi-trust" />
        <KpiCard label="Completed" value={analytics.totals.completed} icon={CheckCircle} testid="analytics-kpi-completed" />
        <KpiCard label="Cancelled" value={analytics.totals.cancelled} icon={XCircle} testid="analytics-kpi-cancelled" />
        <KpiCard label="Total" value={analytics.totals.total} icon={Calendar} tone="accent" testid="analytics-kpi-total" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6">
          <h2 className="font-serif text-2xl text-primary mb-3">Appointments — last 8 weeks</h2>
          <div style={{ width: "100%", height: 260 }} data-testid="analytics-weekly-chart">
            <ResponsiveContainer>
              <BarChart data={analytics.weekly_trend}>
                <CartesianGrid stroke="#E5E3DB" strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fill: "#5C6B61", fontSize: 11 }} />
                <YAxis tick={{ fill: "#5C6B61", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E3DB", borderRadius: 8 }} />
                <Bar dataKey="appointments" fill="#2A3B32" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-white p-6">
          <h2 className="font-serif text-2xl text-primary mb-3">Trust Index breakdown</h2>
          <TrustRadar criteria={criteria} />
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-border bg-white p-6">
        <h2 className="font-serif text-2xl text-primary mb-4">Reputation snapshot</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="overline">Average rating</div>
            <div className="mt-2 font-serif text-3xl text-primary">{analytics.reputation.avg_rating}</div>
            <div className="text-xs text-muted-foreground">out of 5.0</div>
          </div>
          <div>
            <div className="overline">Referrals · last month</div>
            <div className="mt-2 font-serif text-3xl text-primary">{analytics.reputation.referrals_last_month}</div>
            <div className="text-xs text-muted-foreground">from peer network</div>
          </div>
          <div>
            <div className="overline">Patient satisfaction</div>
            <div className="mt-2 font-serif text-3xl text-primary">{analytics.reputation.patient_satisfaction}%</div>
            <div className="text-xs text-muted-foreground">post-visit survey</div>
          </div>
        </div>
      </div>
      </UpgradeGate>
    </AppShell>
  );
}
